/**
 * DungeonConfig.ts
 * Dungeon identity, personality, and geometry configuration panel.
 * Builds into the "Dungeon Info" section of the right sidebar.
 * Reads/writes directly to the OctileDungeon reference.
 */

import type { OctileDungeon } from '../types/OctileTypes.ts'
import { CORRIDOR_SCALES } from '../types/OctileTypes.ts'
import type { DungeonPersonality } from '../types/GrowthTypes.ts'
import { PERSONALITY_PRESETS, createDefaultDNA } from '../types/GrowthTypes.ts'

// ─── Types ──────────────────────────────────────────────────

export interface DungeonConfigCallbacks {
    onNameChanged?: (name: string) => void
    onPersonalityChanged?: (personality: DungeonPersonality) => void
    onCorridorScaleChanged?: (scale: 'spacious' | 'standard' | 'cramped') => void
    onRegenerateRequested?: () => void
}

const PERSONALITY_DESCRIPTIONS: Record<DungeonPersonality, string> = {
    aggressive: 'More enemies, harder fights, fast growth',
    cunning: 'Traps, hidden passages, puzzles',
    hoarding: 'Rich treasure, well-guarded vaults',
    expansive: 'Rapid room growth, wide layouts',
    defensive: 'Reinforced rooms, barriers, slow growth',
}

const CORRIDOR_DESCRIPTIONS: Record<string, string> = {
    spacious: '5.3m — 4 abreast, grand halls',
    standard: '3.6m — classic dungeon corridors',
    cramped: '2.7m — tight single-file passages',
}

// ─── DungeonConfig ──────────────────────────────────────────

export class DungeonConfig {
    private container: HTMLElement
    private dungeon: OctileDungeon | null = null
    private callbacks: DungeonConfigCallbacks

    constructor(container: HTMLElement, callbacks: DungeonConfigCallbacks = {}) {
        this.container = container
        this.callbacks = callbacks
        this.renderEmpty()
    }

    /** Bind to a dungeon and render full config panel */
    setDungeon(dungeon: OctileDungeon, roomCount: number): void {
        this.dungeon = dungeon
        this.render(roomCount)
    }

    /** Update room count display without full re-render */
    updateRoomCount(count: number): void {
        const el = this.container.querySelector('.dc-room-count')
        if (el) el.textContent = String(count)
    }

    /** Get current dungeon reference */
    getDungeon(): OctileDungeon | null {
        return this.dungeon
    }

    // ─── Render ─────────────────────────────────────────

    private renderEmpty(): void {
        this.container.innerHTML = `
            <p class="panel-desc">
                No dungeon generated yet. Hit <strong style="color: var(--green);">Generate Dungeon</strong> in the left panel to create a new layout.
            </p>
            <ul class="panel-tips">
                <li>Click cells to select and inspect</li>
                <li>Right-drag to orbit, scroll to zoom</li>
                <li>Ctrl+click to multi-select cells</li>
            </ul>
        `
    }

    private render(roomCount: number): void {
        if (!this.dungeon) return this.renderEmpty()

        const d = this.dungeon
        const dna = d.dna
        const preset = PERSONALITY_PRESETS[dna.personality]

        this.container.innerHTML = `
            <div class="dc-panel">
                <!-- Identity -->
                <input class="editable-name dc-name" type="text" value="${this.escapeAttr(d.name)}"
                       title="Click to rename your dungeon" maxlength="40">
                <div class="dc-meta">
                    <span class="dc-id" title="Dungeon ID: ${d.id}">${d.id.slice(0, 8)}...</span>
                    <span class="dc-version">v${d.version}</span>
                </div>

                <div class="info-divider" style="margin: 6px 0;"></div>

                <!-- Stats Row -->
                <div class="dc-stats">
                    <div class="info-row"><span>Rooms:</span> <span class="dc-room-count">${roomCount}</span></div>
                    <div class="info-row"><span>Grid:</span> <span>${d.octagonCols}&times;${d.octagonRows}</span></div>
                    <div class="info-row"><span>Seed:</span> <span>${d.seed}</span></div>
                </div>

                <div class="info-divider" style="margin: 6px 0;"></div>

                <!-- Personality -->
                <div class="dc-field">
                    <label class="dc-label">Personality</label>
                    <select class="dc-select dc-personality">
                        ${this.personalityOptions(dna.personality)}
                    </select>
                    <div class="dc-hint dc-personality-desc">${PERSONALITY_DESCRIPTIONS[dna.personality]}</div>
                </div>

                <!-- Growth Rate -->
                <div class="dc-field">
                    <label class="dc-label">Growth Rate</label>
                    <div class="dc-slider-row">
                        <input type="range" class="dc-slider dc-growth-rate" min="0.5" max="2.5" step="0.1" value="${dna.growthRate}">
                        <span class="dc-slider-value dc-growth-value">${dna.growthRate.toFixed(1)}&times;</span>
                    </div>
                </div>

                <!-- Max Rooms -->
                <div class="dc-field">
                    <label class="dc-label">Max Rooms</label>
                    <div class="dc-slider-row">
                        <input type="range" class="dc-slider dc-max-rooms" min="10" max="50" step="1" value="${dna.maxRooms}">
                        <span class="dc-slider-value dc-max-rooms-value">${dna.maxRooms}</span>
                    </div>
                </div>

                <!-- Max Levels -->
                <div class="dc-field">
                    <label class="dc-label">Max Levels</label>
                    <div class="dc-slider-row">
                        <input type="range" class="dc-slider dc-max-levels" min="1" max="5" step="1" value="${dna.maxLevels}">
                        <span class="dc-slider-value dc-max-levels-value">${dna.maxLevels}</span>
                    </div>
                </div>

                <div class="info-divider" style="margin: 6px 0;"></div>

                <!-- Corridor Scale -->
                <div class="dc-field">
                    <label class="dc-label">Corridor Scale</label>
                    <div class="dc-radio-group">
                        ${this.corridorRadios(d.corridorScale)}
                    </div>
                </div>

                <div class="info-divider" style="margin: 6px 0;"></div>

                <!-- Derived from Personality (read-only) -->
                <div class="dc-derived">
                    <label class="dc-label dc-label-muted">Personality Traits</label>
                    <div class="dc-chips-row">
                        <span class="dc-chip-label">Rooms:</span>
                        ${this.renderChips(preset.preferredRoomTypes ?? [])}
                    </div>
                    <div class="dc-chips-row">
                        <span class="dc-chip-label">Enemies:</span>
                        ${this.renderChips(preset.enemyAffinity ?? [])}
                    </div>
                    <div class="dc-chips-row">
                        <span class="dc-chip-label">Traps:</span>
                        ${this.renderChips(preset.trapAffinity ?? [])}
                    </div>
                </div>
            </div>
        `

        this.bindEvents()
    }

    // ─── Event Binding ──────────────────────────────────

    private bindEvents(): void {
        if (!this.dungeon) return
        const d = this.dungeon

        // Name
        const nameInput = this.container.querySelector('.dc-name') as HTMLInputElement
        nameInput?.addEventListener('change', () => {
            d.name = nameInput.value.trim() || 'Unnamed Dungeon'
            d.modifiedAt = new Date().toISOString()
            this.callbacks.onNameChanged?.(d.name)
        })

        // Personality
        const personalitySelect = this.container.querySelector('.dc-personality') as HTMLSelectElement
        personalitySelect?.addEventListener('change', () => {
            const p = personalitySelect.value as DungeonPersonality
            const newDna = createDefaultDNA(p)
            d.dna = { ...d.dna, ...newDna, rules: d.dna.rules }
            d.modifiedAt = new Date().toISOString()

            // Update derived displays
            const desc = this.container.querySelector('.dc-personality-desc')
            if (desc) desc.textContent = PERSONALITY_DESCRIPTIONS[p]

            // Update slider values to match preset
            this.updateSlider('.dc-growth-rate', '.dc-growth-value', d.dna.growthRate, v => `${v.toFixed(1)}\u00d7`)
            this.updateSlider('.dc-max-rooms', '.dc-max-rooms-value', d.dna.maxRooms)
            this.updateSlider('.dc-max-levels', '.dc-max-levels-value', d.dna.maxLevels)

            // Re-render derived chips
            const derived = this.container.querySelector('.dc-derived')
            if (derived) {
                const preset = PERSONALITY_PRESETS[p]
                derived.innerHTML = `
                    <label class="dc-label dc-label-muted">Personality Traits</label>
                    <div class="dc-chips-row">
                        <span class="dc-chip-label">Rooms:</span>
                        ${this.renderChips(preset.preferredRoomTypes ?? [])}
                    </div>
                    <div class="dc-chips-row">
                        <span class="dc-chip-label">Enemies:</span>
                        ${this.renderChips(preset.enemyAffinity ?? [])}
                    </div>
                    <div class="dc-chips-row">
                        <span class="dc-chip-label">Traps:</span>
                        ${this.renderChips(preset.trapAffinity ?? [])}
                    </div>
                `
            }

            this.callbacks.onPersonalityChanged?.(p)
        })

        // Growth Rate slider
        this.bindSlider('.dc-growth-rate', '.dc-growth-value',
            v => { d.dna.growthRate = v; d.modifiedAt = new Date().toISOString() },
            v => `${v.toFixed(1)}\u00d7`
        )

        // Max Rooms slider
        this.bindSlider('.dc-max-rooms', '.dc-max-rooms-value',
            v => { d.dna.maxRooms = Math.round(v); d.modifiedAt = new Date().toISOString() },
            v => String(Math.round(v))
        )

        // Max Levels slider
        this.bindSlider('.dc-max-levels', '.dc-max-levels-value',
            v => { d.dna.maxLevels = Math.round(v); d.modifiedAt = new Date().toISOString() },
            v => String(Math.round(v))
        )

        // Corridor Scale radios
        this.container.querySelectorAll<HTMLInputElement>('.dc-corridor-radio').forEach(radio => {
            radio.addEventListener('change', () => {
                const scale = radio.value as 'spacious' | 'standard' | 'cramped'
                d.corridorScale = scale
                d.cornerCutRatio = CORRIDOR_SCALES[scale]
                d.modifiedAt = new Date().toISOString()
                this.callbacks.onCorridorScaleChanged?.(scale)
            })
        })
    }

    // ─── Helpers ────────────────────────────────────────

    private bindSlider(
        sliderSel: string, valueSel: string,
        onUpdate: (v: number) => void,
        format: (v: number) => string = v => String(v)
    ): void {
        const slider = this.container.querySelector(sliderSel) as HTMLInputElement
        const display = this.container.querySelector(valueSel)
        if (!slider) return

        slider.addEventListener('input', () => {
            const v = parseFloat(slider.value)
            if (display) display.textContent = format(v)
            onUpdate(v)
        })
    }

    private updateSlider(
        sliderSel: string, valueSel: string,
        value: number, format?: (v: number) => string
    ): void {
        const slider = this.container.querySelector(sliderSel) as HTMLInputElement
        const display = this.container.querySelector(valueSel)
        if (slider) slider.value = String(value)
        if (display) display.textContent = format ? format(value) : String(value)
    }

    private personalityOptions(current: DungeonPersonality): string {
        const types: DungeonPersonality[] = ['aggressive', 'cunning', 'hoarding', 'expansive', 'defensive']
        return types.map(p =>
            `<option value="${p}" ${p === current ? 'selected' : ''}>${p.charAt(0).toUpperCase() + p.slice(1)}</option>`
        ).join('')
    }

    private corridorRadios(current: string): string {
        const scales: Array<'spacious' | 'standard' | 'cramped'> = ['spacious', 'standard', 'cramped']
        return scales.map(s => `
            <label class="dc-radio-label">
                <input type="radio" name="corridorScale" value="${s}"
                       class="dc-corridor-radio" ${s === current ? 'checked' : ''}>
                <span class="dc-radio-text">${s.charAt(0).toUpperCase() + s.slice(1)}</span>
                <span class="dc-radio-hint">${CORRIDOR_DESCRIPTIONS[s]}</span>
            </label>
        `).join('')
    }

    private renderChips(items: string[]): string {
        return items.map(item =>
            `<span class="dc-chip">${item.replace(/_/g, ' ')}</span>`
        ).join('')
    }

    private escapeAttr(s: string): string {
        return s.replace(/"/g, '&quot;').replace(/</g, '&lt;')
    }
}
